import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LogIn, AlertCircle, CheckCircle } from "lucide-react";

interface ClientLoginDialogProps {
  triggerButton: React.ReactNode;
}

const ClientLoginDialog: React.FC<ClientLoginDialogProps> = ({ triggerButton }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [error, setError] = useState("");
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!clientId.trim()) {
      setError("Veuillez saisir votre identifiant client");
      return;
    }
    
    // Normaliser l'ID client (minuscules, remplacer espaces par tirets)
    const normalizedId = clientId.toLowerCase().trim().replace(/\s+/g, '-');
    
    // Rediriger vers la page d'authentification du client
    setIsOpen(false);
    navigate(`/${normalizedId}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {triggerButton}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="w-5 h-5 text-primary" />
            Se connecter à son espace
          </DialogTitle>
          <DialogDescription>
            Saisissez votre identifiant client pour accéder à votre espace de transformation digitale
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="clientId">Identifiant client *</Label>
            <Input
              id="clientId"
              placeholder="ex: demo-axylis, menuiserie-combes..."
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
              className="w-full"
            />
            <p className="text-sm text-muted-foreground">
              L'identifiant vous a été communiqué par votre consultant Ailix
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsOpen(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button type="submit" variant="hero" className="flex-1">
              Continuer
            </Button>
          </div>
        </form>

        <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-primary mb-1">Accès sécurisé par email</p>
              <p className="text-muted-foreground">
                Après avoir saisi votre identifiant, vous recevrez un lien magique 
                par email pour accéder à votre espace en toute sécurité.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ClientLoginDialog;
